import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  ErrorMessage,
  Mtds,
  OwnerId,
  useAuth,
  useErrorToast,
  useGetOwnerNameQueries
} from 'prove-common'
import {
  delegationTargetPath,
  findByOwnerPath,
  getDelegationTotals,
  hasTerminalDelegationAncestor,
  setQuantityAtPath,
  type DelegationNode
} from '../../utils/taskDelegationUtils'
import {
  clearVisibleDelegationsAtPath,
  fillDelegationRemainder
} from '../../utils/delegationActions'
import { HIDDEN_DELEGATION_UNITS } from '../../config/hiddenDelegationUnits'
import {
  isDelegationPathHidden,
  truncateBeforeHiddenDelegationUnit
} from '../../utils/delegationVisibility'
import {
  DELEGATION_PATH_PARAM,
  OWNER_TYPE_LABEL,
  parseDelegationPath,
  serializeDelegationPath
} from '../../utils/delegationExplorerPath'
import { useDelegationLevel } from './useDelegationLevel'
import { fetchLeafPaths } from './fetchOrgSubtree'
import { DelegationBranchCard } from './DelegationBranchCard'
import { DelegationLeafCard } from './DelegationLeafCard'
import { QuantityStepper } from '../quantity-stepper/QuantityStepper'

type DelegationExplorerProps = {
  plannedQuantity: number
  delegations: DelegationNode[]
  onChange: (delegations: DelegationNode[]) => void
  canEditQuantity: (ownerId: OwnerId) => boolean
  /**
   * Nivået fordelingen starter på. Er tom når brukeren skal velge divisjon selv,
   * og inneholder tilsynsdivisjonen når planen hører til en.
   */
  rootPath: OwnerId[]
  /** Navnet på tilsynsdivisjonen fordelingen starter i. */
  rootLabel?: string
  /**
   * Planen hører til en tilsynsdivisjon som ennå ikke finnes i
   * organisasjonsstrukturen, så brukeren må velge divisjon selv.
   */
  navigation: ReactNode
  hiddenUnits?: ReadonlySet<string>
}

const capitalize = (value: string): string =>
  value.replace(/./, (match) => match.toUpperCase())

/**
 * Fordelingen vist som ett nivå om gangen: divisjon, avdeling, seksjon og team.
 * Kortene bygges på organisasjonsstrukturen, slik at alle enheter kan tildeles
 * prøver uten å legges til først. Antall settes kun på nederste nivå – nivåene
 * over viser summen av det som ligger under.
 */
export const DelegationExplorer = ({
  plannedQuantity,
  delegations,
  onChange,
  canEditQuantity,
  rootPath,
  rootLabel,
  navigation,
  hiddenUnits = HIDDEN_DELEGATION_UNITS
}: Readonly<DelegationExplorerProps>) => {
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  const [isFilling, setIsFilling] = useState(false)
  const showError = useErrorToast('hente organisasjonsstrukturen')
  const path = useMemo(() => {
    const parsed = parseDelegationPath(searchParams.get(DELEGATION_PATH_PARAM))
    // Fordelingen kan aldri navigere ut av tilsynsdivisjonen den hører til.
    const startsAtRoot = rootPath.every(
      (ownerId, index) =>
        parsed[index]?.type === ownerId.type &&
        parsed[index]?.owner === ownerId.owner
    )
    return truncateBeforeHiddenDelegationUnit(
      startsAtRoot ? parsed : rootPath,
      hiddenUnits
    )
  }, [searchParams, rootPath, hiddenUnits])

  const level = useDelegationLevel(delegations, path, hiddenUnits)
  // Resolve breadcrumb names for the current organization path.
  const resolvedNames = useGetOwnerNameQueries([
    ...path.map((ownerId) => ownerId.toString())
  ])

  const { allocated: samplesDelegated, remainder } = getDelegationTotals(
    delegations,
    rootPath,
    plannedQuantity
  )

  const getName = (ownerId: OwnerId): string => {
    const resolved = resolvedNames.data.find(
      (owner) => owner.id === ownerId.toString()
    )
    return capitalize(resolved?.name ?? ownerId.owner)
  }

  const buildSearch = (nextPath: OwnerId[]): string => {
    const next = new URLSearchParams(searchParams)
    const value = serializeDelegationPath(nextPath)
    if (value.length > 0) next.set(DELEGATION_PATH_PARAM, value)
    else next.delete(DELEGATION_PATH_PARAM)
    return next.toString()
  }

  const current = path.at(-1)
  const isRoot = current == null
  const currentDelegation = findByOwnerPath(delegations, path)
  const isAtScopedRoot =
    path.length === rootPath.length &&
    rootPath.every(
      (ownerId, index) => ownerId.toString() === path[index]?.toString()
    )
  const isLegacyRootTarget =
    isAtScopedRoot &&
    current != null &&
    currentDelegation != null &&
    currentDelegation.children.length === 0 &&
    level.items.length > 0
  const isBlockedByLegacyDelegation = (ownerId: OwnerId): boolean =>
    hasTerminalDelegationAncestor(delegations, [...path, ownerId])
  const canEditItem = (ownerId: OwnerId): boolean =>
    canEditQuantity(ownerId) && !isBlockedByLegacyDelegation(ownerId)
  const visibleItems = level.items.filter(
    (item) => !isDelegationPathHidden([...path, item.ownerId], hiddenUnits)
  )
  const canEditScopedRoot =
    current != null && canEditQuantity(current) && isAtScopedRoot
  const canClearAny =
    (canEditScopedRoot && (currentDelegation?.retainedQuantity ?? 0) > 0) ||
    visibleItems.some(
      (item) => canEditItem(item.ownerId) && item.plannedQuantity > 0
    )
  const canFillAny =
    (isLegacyRootTarget && canEditScopedRoot) ||
    visibleItems.some((item) => canEditItem(item.ownerId))
  const leafItems = visibleItems.filter((item) => item.isLeaf)
  const branchItems = visibleItems.filter((item) => !item.isLeaf)

  const handleLeafChange = (ownerId: OwnerId, quantity: number): void => {
    if (isFilling || !canEditItem(ownerId)) return
    onChange(setQuantityAtPath(delegations, [...path, ownerId], quantity))
  }

  const handleClearAll = (): void => {
    if (isFilling) return
    onChange(
      clearVisibleDelegationsAtPath(
        delegations,
        path,
        hiddenUnits,
        canEditQuantity
      )
    )
  }

  /**
   * Fordeler restantallet likt utover de faktiske bladnodene under kortene på
   * skjermen, slik at hvert team får like mange prøver uansett hvor dypt det
   * ligger. Organisasjonsstrukturen under kortene hentes først når brukeren
   * trykker, og gjenbrukes fra hurtigbufferen hvis den allerede er hentet.
   */
  const handleFillRemainder = async (): Promise<void> => {
    if (remainder <= 0 || isFilling) return
    setIsFilling(true)
    try {
      if (isLegacyRootTarget && canEditScopedRoot) {
        onChange(fillDelegationRemainder(delegations, [path], remainder))
        return
      }
      const leafPaths: OwnerId[][] = []
      for (const item of visibleItems) {
        if (!canEditItem(item.ownerId)) continue
        if (item.isLegacyTarget || item.isOrphan) {
          // Existing terminals and orphan units are already allocation targets.
          leafPaths.push([item.ownerId])
          continue
        }
        const fetchedPaths = await fetchLeafPaths(
          queryClient,
          authFetch,
          item.ownerId
        )
        for (const leafPath of fetchedPaths) {
          const targetPath = delegationTargetPath(delegations, [
            ...path,
            ...leafPath
          ]).slice(path.length)
          const targetOwner = targetPath.at(-1)
          if (
            targetPath.length > 0 &&
            targetOwner != null &&
            canEditQuantity(targetOwner) &&
            !isDelegationPathHidden([...path, ...targetPath], hiddenUnits)
          ) {
            leafPaths.push(targetPath)
          }
        }
      }
      if (leafPaths.length === 0) return
      onChange(
        fillDelegationRemainder(
          delegations,
          leafPaths.map((leafPath) => [...path, ...leafPath]),
          remainder
        )
      )
    } catch (error) {
      showError(error)
    } finally {
      setIsFilling(false)
    }
  }

  if (level.isError)
    return (
      <ErrorMessage
        error={level.error}
        actionButton={
          <Mtds.Button onClick={() => void level.refetch()}>
            Prøv igjen
          </Mtds.Button>
        }
      />
    )

  const renderCard = (item: (typeof level.items)[number]) =>
    item.isLeaf ? (
      <DelegationLeafCard
        key={item.ownerId.toString()}
        item={item}
        typeLabel={OWNER_TYPE_LABEL[item.ownerId.type]}
        remainder={remainder}
        canEdit={canEditItem(item.ownerId)}
        hideControls={isBlockedByLegacyDelegation(item.ownerId)}
        isMutating={isFilling}
        onChange={(quantity) => handleLeafChange(item.ownerId, quantity)}
      />
    ) : (
      <DelegationBranchCard
        key={item.ownerId.toString()}
        item={item}
        typeLabel={OWNER_TYPE_LABEL[item.ownerId.type]}
        to={{ search: buildSearch([...path, item.ownerId]) }}
        canEdit={canEditItem(item.ownerId)}
        isMutating={isFilling}
        onChange={(quantity) => handleLeafChange(item.ownerId, quantity)}
      />
    )

  return (
    <Mtds.Grid data-gap={5}>
      {navigation}
      {!isRoot && (
        <Mtds.Breadcrumbs data-size='sm'>
          <ol>
            {rootPath.length === 0 && (
              <li>
                <Link to={{ search: buildSearch([]) }}>Fordeling</Link>
              </li>
            )}
            {path.map((ownerId, index) => {
              const label =
                index === 0
                  ? (rootLabel ?? getName(ownerId))
                  : `${OWNER_TYPE_LABEL[ownerId.type]} ${getName(ownerId).toLowerCase()}`
              const isCurrent = index === path.length - 1
              return (
                <li key={ownerId.toString()}>
                  {isCurrent ? (
                    <span>{label}</span>
                  ) : (
                    <Link
                      to={{ search: buildSearch(path.slice(0, index + 1)) }}
                    >
                      {label}
                    </Link>
                  )}
                </li>
              )
            })}
          </ol>
        </Mtds.Breadcrumbs>
      )}

      {isLegacyRootTarget && (
        <Mtds.Card>
          <Mtds.Grid data-gap={2}>
            <Mtds.Muted>
              Fordelingen er stoppet på dette nivået. Underenhetene kan ikke
              redigeres.
            </Mtds.Muted>
            <QuantityStepper
              label={`Prøver til ${rootLabel ?? getName(current!)}`}
              value={currentDelegation.plannedQuantity}
              min={0}
              disabled={!canEditQuantity(current!) || isFilling}
              onChange={(quantity) =>
                onChange(setQuantityAtPath(delegations, path, quantity))
              }
            />
          </Mtds.Grid>
        </Mtds.Card>
      )}

      <Mtds.Flex
        data-justify='space-between'
        data-align='end'
        data-gap={4}
        className='max-sm:flex-col max-sm:items-start'
      >
        {!isRoot && (
          <Mtds.Grid data-gap={1}>
            <Mtds.Muted>{OWNER_TYPE_LABEL[current.type]}</Mtds.Muted>
            <Mtds.Heading as='h3' data-size='sm'>
              {path.length === 1
                ? (rootLabel ?? getName(current))
                : getName(current)}
            </Mtds.Heading>
          </Mtds.Grid>
        )}
        <Mtds.Flex data-gap={2} data-nowrap>
          <Mtds.Button
            data-variant='secondary'
            onClick={handleClearAll}
            disabled={!canClearAny || samplesDelegated === 0 || isFilling}
          >
            Fjern alle
          </Mtds.Button>
          <Mtds.Button
            data-variant='secondary'
            onClick={() => void handleFillRemainder()}
            disabled={!canFillAny || remainder <= 0 || isFilling}
          >
            Fyll resten likt
          </Mtds.Button>
        </Mtds.Flex>
      </Mtds.Flex>
      {level.isLoading && level.items.length === 0 && <Mtds.Skeleton />}

      {level.isLoadingChildCounts && level.items.length > 0 && (
        <Mtds.Skeleton />
      )}

      {branchItems.length > 0 && (
        <Mtds.Grid
          data-gap={4}
          className='grid-cols-1 md:grid-cols-2 xl:grid-cols-3 [&>*]:min-w-0 [&>*]:w-full'
        >
          {branchItems.map(renderCard)}
        </Mtds.Grid>
      )}
      {leafItems.length > 0 && (
        <Mtds.Grid
          data-gap={4}
          className='grid-cols-1 md:grid-cols-2 xl:grid-cols-3 [&>*]:min-w-0 [&>*]:w-full'
        >
          {leafItems.map(renderCard)}
        </Mtds.Grid>
      )}
    </Mtds.Grid>
  )
}
